import { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { loanActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { RefreshEvents } from '../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import { CommonLoanSummary } from '../common/LoanComponents';
import DatePicker from 'react-datepicker';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { CommonLoanSummaryInvokingFeatureType } from '../common/constants/CommonLoanSummaryInvokingFeatureType';

const styles = {
    buttonMarginRight: { marginRight: '10px' },
    buttonContainer: {
        textAlign: 'right',
        borderTop: '2px solid silver',
        padding: '10px 10px',
    },
    form: { paddingLeft: '5px', paddingTop: '10px' },
    formRow: { marginBottom: '0' },
    overdue: { color: 'red' },
};

const BuildRentConversionModal = () => {
    const { isOpen, loanId } = useSelector((state) => state.loan.buildRentConversionModal);
    const { dispatch } = store;

    const [loan, set_loan] = useState(null);
    const [conversionNotes, set_conversionNotes] = useState('');
    const [conversionDate, set_conversionDate] = useState(new Date());

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const _handleClose = () =>
        dispatch(
            loanActions.setBuildRentConversionModal({
                buildRentConversionModal: { isOpen: false },
            })
        );

    const onSubmit = async (e) => {
        e.preventDefault();

        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = {
                conversionDate: new Date(conversionDate).toLocaleDateString('en-us'),
                conversionNotes,
            };

            const response = await loanApi.setBuildRentConvert(loanId, model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Loan converted successfully.');
            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    useEffect(() => {
        async function loanIdChanged() {
            if (loanId && loanId > 0) {
                await loadLoanInfo(loanId);
            } else {
                resetState();
            }
            set_loadingOpen(false);
        }

        loanIdChanged();
    }, [isOpen, loanId]);

    const resetState = () => {
        set_loan(null);
        set_conversionDate(new Date());
        set_conversionNotes('');
    };

    const loadLoanInfo = async (id) => {
        set_loadingMessage('Loading...');
        set_loadingOpen(true);

        const response = await loanApi.getBuildRentConvert(id);
        if (response.statusCode !== 200) {
            set_loadingOpen(false);
            return;
        }
        const loan = response.result;
        set_loan(loan);
        set_loadingOpen(false);
    };

    if (!isOpen || !loan) {
        return '';
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '500px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form
                                    name='conversionForm'
                                    onSubmit={(event) => onSubmit(event)}
                                >
                                    <div>
                                        <div
                                            className='page-header'
                                            role='banner'
                                        >
                                            <h1 style={{ whiteSpace: 'nowrap' }}>Convert to Rental</h1>
                                            <button
                                                type='button'
                                                className='close'
                                                data-dismiss='modal'
                                                aria-label='Close'
                                                onClick={_handleClose}
                                            >
                                                <span aria-hidden='true'>&times;</span>
                                            </button>
                                        </div>
                                        <div>
                                            <div
                                                style={styles.form}
                                                className={'standard-form'}
                                            >
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div className='columns small-12 large-4'>
                                                        <label htmlFor='conversionDeadline'>Conv. Deadline</label>
                                                        <DatePicker
                                                            id='conversionDate'
                                                            selected={
                                                                loan?.conversionDeadline
                                                                    ? new Date(loan.conversionDeadline)
                                                                    : null
                                                            }
                                                            onChange={(e) => set_conversionDate(e)}
                                                            disabled={true}
                                                        />
                                                    </div>
                                                    <div
                                                        className='columns small-12 large-8'
                                                        style={{
                                                            marginTop: '32px',
                                                        }}
                                                    >
                                                        {loan?.deadlineMessage && (
                                                            <span style={loan?.isOverDue ? styles.overdue : null}>
                                                                {loan.deadlineMessage}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div className='columns small-12 large-5'>
                                                        <label htmlFor='lostTypeId'>
                                                            Move to Rental on <span className='req'>*</span>
                                                        </label>
                                                        <DatePicker
                                                            id='conversionDate'
                                                            selected={conversionDate}
                                                            onChange={(e) => set_conversionDate(e)}
                                                            required={true}
                                                            showYearDropdown
                                                        />
                                                    </div>
                                                </div>
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='reason'>Conversion Notes</label>
                                                        <textarea
                                                            style={{
                                                                width: '100%',
                                                            }}
                                                            rows={3}
                                                            id='conversionNotes'
                                                            value={conversionNotes}
                                                            onChange={(e) => set_conversionNotes(e.target.value)}
                                                            required={false}
                                                            placeholder='Enter any relevant notes...'
                                                            maxLength={2000}
                                                        />
                                                    </div>
                                                </div>
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div
                                                        className='columns small-12 large-12'
                                                        style={{
                                                            color: 'darkred',
                                                        }}
                                                    >
                                                        Please update DSCR as soon as information is obtained!
                                                    </div>
                                                </div>
                                                <CommonLoanSummary
                                                    loan={loan}
                                                    invokingFeature={CommonLoanSummaryInvokingFeatureType.BuildRentConversion}
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className='button-footer'
                                            style={styles.buttonContainer}
                                        >
                                            <button
                                                style={styles.buttonMarginRight}
                                                type='submit'
                                                className='button primary'
                                            >
                                                Submit
                                            </button>
                                            <button
                                                type='button'
                                                className='button hollow'
                                                data-dismiss='modal'
                                                onClick={_handleClose}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </>
    );
};

export default BuildRentConversionModal;
